import { loadStripe } from '@stripe/stripe-js';
import { supabase } from "@/integrations/supabase/client";
import { DigitalContent } from "@/types/digitalContent";

// Inicializar Stripe (usar variável de ambiente)
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey && stripeKey !== '' ? loadStripe(stripeKey) : null;

export interface StripeCheckoutSession {
  id: string;
  url: string;
  payment_status: 'pending' | 'completed' | 'failed';
}

/**
 * Criar sessão de checkout do Stripe para compra de conteúdo digital
 */
export async function createStripeCheckoutSession(
  content: DigitalContent
): Promise<StripeCheckoutSession> {
  try {
    console.log("💳 [createStripeCheckoutSession] Criando sessão para:", content.title);

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error("Usuário não autenticado");
    }

    const buyerId = userData.user.id;
    const userEmail = userData.user.email;

    // Verificar se o usuário já possui este conteúdo (completed)
    const { data: completedPurchase, error: completedError } = await supabase
      .from("digital_content_purchases")
      .select("id, payment_status")
      .eq("digital_content_id", content.id)
      .eq("buyer_id", buyerId)
      .eq("payment_status", "completed")
      .single();

    if (completedError && completedError.code !== "PGRST116") {
      console.error("💳 [createStripeCheckoutSession] Erro ao verificar compra completed:", completedError);
      throw completedError;
    }

    if (completedPurchase) {
      throw new Error("Você já possui este conteúdo");
    }

    // Verificar e limpar compras pendentes antigas (mais de 1 hora)
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const { data: pendingPurchases, error: pendingError } = await supabase
      .from("digital_content_purchases")
      .select("id, created_at")
      .eq("digital_content_id", content.id)
      .eq("buyer_id", buyerId)
      .eq("payment_status", "pending")
      .lt("created_at", oneHourAgo);

    if (pendingError && pendingError.code !== "PGRST116") {
      console.error("💳 [createStripeCheckoutSession] Erro ao verificar compras pending:", pendingError);
    }

    // Limpar compras pendentes antigas
    if (pendingPurchases && pendingPurchases.length > 0) {
      console.log("🧹 [createStripeCheckoutSession] Limpando compras pendentes antigas:", pendingPurchases.length);
      await supabase
        .from("digital_content_purchases")
        .delete()
        .eq("digital_content_id", content.id)
        .eq("buyer_id", buyerId)
        .eq("payment_status", "pending")
        .lt("created_at", oneHourAgo);
    }

    // Verificar se ainda existe alguma compra pendente recente
    const { data: recentPending, error: recentError } = await supabase
      .from("digital_content_purchases")
      .select("id")
      .eq("digital_content_id", content.id)
      .eq("buyer_id", buyerId)
      .eq("payment_status", "pending")
      .single();

    if (recentError && recentError.code !== "PGRST116") {
      console.error("💳 [createStripeCheckoutSession] Erro ao verificar pending recente:", recentError);
    }

    if (recentPending) {
      throw new Error("Você já tem uma compra em andamento para este conteúdo. Aguarde alguns minutos e tente novamente.");
    }

    // Para conteúdos gratuitos, não usar Stripe
    if (content.price_cents === 0) {
      throw new Error("Conteúdos gratuitos não precisam de pagamento via Stripe");
    }

    // Criar registro de compra pendente no banco
    const { data: purchaseData, error: purchaseError } = await supabase
      .from("digital_content_purchases")
      .insert({
        digital_content_id: content.id,
        buyer_id: buyerId,
        purchase_price_cents: content.price_cents,
        payment_status: "pending",
        payment_method: "stripe"
      })
      .select("id")
      .single();

    if (purchaseError) {
      console.error("💳 [createStripeCheckoutSession] Erro ao criar compra pendente:", purchaseError);
      throw purchaseError;
    }

    const purchaseId = purchaseData.id;

    // Chamar função do Supabase Edge Function para criar sessão do Stripe
    const { data: sessionData, error: sessionError } = await supabase.functions.invoke('create-stripe-session', {
      body: {
        contentId: content.id,
        contentTitle: content.title,
        contentDescription: content.description,
        priceCents: content.price_cents,
        purchaseId: purchaseId,
        customerEmail: userEmail,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/payment-canceled`
      }
    });

    if (sessionError) {
      console.error("💳 [createStripeCheckoutSession] Erro ao criar sessão:", sessionError);
      
      // Remover compra pendente se falhou
      await supabase
        .from("digital_content_purchases")
        .delete()
        .eq("id", purchaseId);
        
      throw new Error("Erro ao criar sessão de pagamento: " + sessionError.message);
    }

    console.log("✅ [createStripeCheckoutSession] Sessão criada com sucesso:", sessionData.sessionId);

    return {
      id: sessionData.sessionId,
      url: sessionData.url,
      payment_status: 'pending'
    };

  } catch (error) {
    console.error("💳 [createStripeCheckoutSession] Erro:", error);
    throw error;
  }
}

/**
 * Redirecionar para checkout do Stripe
 */
export async function redirectToStripeCheckout(sessionId: string): Promise<void> {
  try {
    if (!stripePromise) {
      throw new Error("Stripe não está configurado (chave não encontrada)");
    }
    
    const stripe = await stripePromise;
    if (!stripe) {
      throw new Error("Stripe não foi carregado corretamente");
    }

    console.log("🔄 [redirectToStripeCheckout] Redirecionando para sessão:", sessionId);

    const { error } = await stripe.redirectToCheckout({
      sessionId: sessionId
    });

    if (error) {
      console.error("💳 [redirectToStripeCheckout] Erro no redirecionamento:", error);
      throw error;
    }

  } catch (error) {
    console.error("💳 [redirectToStripeCheckout] Erro:", error);
    throw error;
  }
}

/**
 * Verificar status de uma sessão de pagamento
 */
export async function verifyPaymentSession(sessionId: string): Promise<{
  status: 'pending' | 'completed' | 'failed';
  purchaseId?: string;
}> {
  try {
    console.log("🔍 [verifyPaymentSession] Verificando sessão:", sessionId);

    const { data, error } = await supabase.functions.invoke('verify-stripe-session', {
      body: { sessionId }
    });

    if (error) {
      console.error("🔍 [verifyPaymentSession] Erro ao verificar:", error);
      throw error;
    }

    console.log("✅ [verifyPaymentSession] Status verificado:", data.status);

    return {
      status: data.status,
      purchaseId: data.purchaseId
    };

  } catch (error) {
    console.error("🔍 [verifyPaymentSession] Erro:", error);
    throw error;
  }
}

/**
 * Processar pagamento bem-sucedido (chamado pelo webhook ou página de sucesso)
 */
export async function processSuccessfulPayment(
  sessionId: string,
  purchaseId: string
): Promise<void> {
  try {
    console.log("✅ [processSuccessfulPayment] Processando pagamento:", { sessionId, purchaseId });

    // Atualizar status da compra para completed
    const { error: updateError } = await supabase
      .from("digital_content_purchases")
      .update({
        payment_status: "completed",
        access_granted_at: new Date().toISOString(),
        stripe_payment_intent_id: sessionId
      })
      .eq("id", purchaseId);

    if (updateError) {
      console.error("✅ [processSuccessfulPayment] Erro ao atualizar compra:", updateError);
      throw updateError;
    }

    console.log("🎉 [processSuccessfulPayment] Pagamento processado com sucesso!");

  } catch (error) {
    console.error("✅ [processSuccessfulPayment] Erro:", error);
    throw error;
  }
} 